import { type GrpcClient } from '@pushwoosh/http-client';
import { EventsServiceClient } from '@pushwoosh/rpc-service-client/pushwoosh/events/EventsServiceClientPb';
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb';

import {
  type Event,
  EventAttribute,
  CreateEventRequest,
  UpdateEventRequest,
  DeleteEventRequest,
  GetEventRequest,
  GetAttributeValuesRequest,
  GetTriggeredCountRequest,
  GetTotalStatisticsRequest,
  GetDetailedStatisticsRequest,
  ListEventsRequest,
  TimeInterval,
} from '@pushwoosh/rpc-service-client/pushwoosh/events/events_pb';
import EventAttributeType = EventAttribute.Type;
import type { ConditionValueType } from '../segments/types';
import type {
  DetailedStatisticsAttributeCondition,
  EventData
} from './types';
import { toAttributeCondition } from './conditions';

type CreateEventParams = {
  applicationCode: string;
  name: string;
  description?: string;
  attributes?: Array<{
    name: string;
    type: ConditionValueType;
  }>;
};

type UpdateEventParams = {
  applicationCode: string;
  name: string;
  description?: string;
  attributes?: Array<{
    name: string;
    type: ConditionValueType;
  }>;
};

type EventsListParams = {
  applicationCode: string;
  searchByName?: string;
  orderBy?: 'name' | 'created';
  orderDirection?: 'asc' | 'desc';
  perPage?: number;
  page?: number;
};

type AttributeValuesParams = {
  applicationCode: string;
  eventName: string;
  attributeName: string;
  search?: string;
  skip?: number;
  limit?: number;
};

type TriggeredCount = {
  uniqueTriggered: number;
  totalTriggered: number;
};

type GetTotalStatisticsParams = {
  applicationCode: string;
  eventNames: string[];
  from: Date;
  to: Date;
};

type GetTotalStatisticsResult = {
  counts: Array<{
    eventName: string;
    count: number;
  }>;
};

type GetDetailedStatisticsParams = {
  applicationCode: string;
  eventName: string;
  interval: 'hour' | 'day' | 'week' | 'month';
  from?: Date;
  to?: Date;
  attributes?: Array<DetailedStatisticsAttributeCondition>;
};

type GetDetailedStatisticsResult = {
  counts: Array<{
    date: Date;
    count: number;
  }>;
};

export type EventsGrpcBridge = {
  create: (params: CreateEventParams) => Promise<string>;
  update: (params: UpdateEventParams) => Promise<boolean>;
  delete: (params: {
    applicationCode: string;
    event: string;
  }) => Promise<boolean>;
  fullList(params: EventsListParams): Promise<{
    events: EventData[];
    totalPages: number;
    page: number;
    perPage: number;
  }>;
  list(params: EventsListParams): Promise<EventData[]>;
  get: (params: {
    applicationCode: string;
    event: string;
  }) => Promise<EventData>;
  getAttributeValues: (params: AttributeValuesParams) => Promise<{
    total: number;
    values: Array<string>;
  }>;
  getTriggeredCount: (params: {
    applicationCode: string;
    eventNames: string[];
  }) => Promise<Record<string, TriggeredCount>>;
  getTotalStatistics: (
    params: GetTotalStatisticsParams,
  ) => Promise<GetTotalStatisticsResult>;
  getDetailedStatistics: (
    params: GetDetailedStatisticsParams,
  ) => Promise<GetDetailedStatisticsResult>;
};

function stringifyEventAttributeType(num: number): ConditionValueType {
  const types: Record<number, ConditionValueType> = {
    [EventAttributeType.INTEGER]: 'integer',
    [EventAttributeType.STRING]: 'string',
    [EventAttributeType.LIST]: 'list',
    [EventAttributeType.DATE]: 'date',
    [EventAttributeType.BOOLEAN]: 'boolean',
    [EventAttributeType.PRICE]: 'decimal',
  };
  return types[num];
}

function toGrpcType(t: ConditionValueType): number {
  const types: Record<string, number> = {
    integer: EventAttributeType.INTEGER,
    string: EventAttributeType.STRING,
    list: EventAttributeType.LIST,
    date: EventAttributeType.DATE,
    boolean: EventAttributeType.BOOLEAN,
    decimal: EventAttributeType.PRICE,
  };
  return types[t];
}

function toGrpcAttr(attr: { name: string; type: ConditionValueType }): EventAttribute {
  return new EventAttribute().setName(attr.name).setType(toGrpcType(attr.type));
}

function event2eventData(event: Event): EventData {
  return {
    name: event.getName(),
    description: event.getDescription(),
    canDelete: event.getCanDelete(),
    storeDays: event.getStoreDays(),
    attributes: event.getAttributesList().map((attr) => ({
      name: attr.getName(),
      type: stringifyEventAttributeType(attr.getType()),
    })),
  };
}

function convertInterval(
  interval: GetDetailedStatisticsParams['interval'],
): TimeInterval {
  const intervals: Record<
    GetDetailedStatisticsParams['interval'],
    TimeInterval
  > = {
    hour: TimeInterval.HOUR,
    day: TimeInterval.DAY,
    week: TimeInterval.WEEK,
    month: TimeInterval.MONTH,
  };
  return intervals[interval];
}

export function createEventsGrpcBridge(
  grpcClient: GrpcClient,
): EventsGrpcBridge {
  const client = grpcClient.createServiceClient(EventsServiceClient);
  return {
    create: async (params) => {
      const req = new CreateEventRequest()
        .setApplication(params.applicationCode)
        .setName(params.name);
      if (params.description) {
        req.setDescription(params.description);
      }
      if (params.attributes) {
        const attrs = params.attributes.map(toGrpcAttr);
        req.setAttributesList(attrs);
      }
      const eventResponse = await client.create(req, null);
      return eventResponse.getName();
    },
    update: async (params) => {
      const req = new UpdateEventRequest()
        .setApplication(params.applicationCode)
        .setName(params.name);
      if (params.description) {
        req.setDescription(params.description);
      }
      if (params.attributes) {
        const attrs = params.attributes.map(toGrpcAttr);
        req.setAttributesList(attrs);
      }
      await client.update(req, null);
      return true;
    },
    delete: async (params) => {
      const req = new DeleteEventRequest()
        .setApplication(params.applicationCode)
        .setName(params.event);
      await client.delete(req, null);
      return true;
    },
    fullList: async (params) => {
      const req = new ListEventsRequest().setApplication(
        params.applicationCode,
      );
      if (params.searchByName) {
        req.setSearchByName(params.searchByName);
      }
      if (params.orderBy) {
        req.setOrderBy(
          params.orderBy === 'name'
            ? ListEventsRequest.OrderBy.NAME
            : ListEventsRequest.OrderBy.CREATED,
        );
      }
      if (params.orderDirection) {
        req.setOrderDirection(
          params.orderDirection === 'asc'
            ? ListEventsRequest.OrderDirection.ASC
            : ListEventsRequest.OrderDirection.DESC,
        );
      }
      if (params.perPage) {
        req.setPerPage(params.perPage);
      }
      if (params.page) {
        req.setPage(params.page);
      }

      const eventsList = await client.list(req, null);
      return {
        events: eventsList.getEventsList().map(event2eventData),
        totalPages: eventsList.getTotalPages(),
        page: eventsList.getPage(),
        perPage: eventsList.getPerPage(),
      };
    },
    // TODO: fullList and list need to be merged
    async list(params: EventsListParams): Promise<EventData[]> {
      const result = await this.fullList(params, true);
      return result.events;
    },
    get: async (params) => {
      const req = new GetEventRequest()
        .setApplication(params.applicationCode)
        .setName(params.event);

      const event = await client.get(req, null);
      return event2eventData(event);
    },
    getAttributeValues: async (params) => {
      const req = new GetAttributeValuesRequest()
        .setApplication(params.applicationCode)
        .setEvent(params.eventName)
        .setAttribute(params.attributeName);
      if (params.search) {
        req.setSearch(params.search);
      }
      if (params.skip) {
        req.setSkip(params.skip);
      }
      if (params.limit) {
        req.setLimit(params.limit);
      }
      const list = await client.getAttributeValues(req, null);
      return {
        total: list.getTotal(),
        values: list.getValuesList(),
      };
    },
    getTriggeredCount: async (params) => {
      const req = new GetTriggeredCountRequest()
        .setApplication(params.applicationCode)
        .setEventsList(params.eventNames);
      const response = await client.getTriggeredCount(req, null);
      const result: Record<string, TriggeredCount> = {};
      const map = response.getCountMap();
      map.forEach((count, key) => {
        result[key] = {
          uniqueTriggered: count.getUniqueTriggered(),
          totalTriggered: count.getTotalTriggered(),
        };
      });

      return result;
    },
    getTotalStatistics: async (params) => {
      const req = new GetTotalStatisticsRequest()
        .setApplication(params.applicationCode)
        .setEventsList(params.eventNames)
        .setDateFrom(Timestamp.fromDate(params.from))
        .setDateTo(Timestamp.fromDate(params.to));
      const response = await client.getTotalStatistics(req, null);
      return {
        counts: response.getMetricsList().map((c) => ({
          eventName: c.getEvent(),
          count: c.getTriggeredCount(),
        })),
      };
    },
    getDetailedStatistics: async (params) => {
      const req = new GetDetailedStatisticsRequest()
        .setApplication(params.applicationCode)
        .setName(params.eventName)
        .setInterval(convertInterval(params.interval));
      if (params.from) {
        req.setDateFrom(Timestamp.fromDate(params.from));
      }
      if (params.to) {
        req.setDateTo(Timestamp.fromDate(params.to));
      }
      if (params.attributes) {
        const attrs = params.attributes.map(toAttributeCondition);
        req.setAttributeConditionsList(attrs);
      }
      const response = await client.getDetailedStatistics(req, null);
      const counts = response.getMetricsList().map((c) => ({
        date: c.getTimestamp()!.toDate(),
        count: c.getTriggeredCount(),
      }));
      return {
        counts,
      };
    },
  };
}
